import React, { MouseEvent } from 'react';

type ItemRecord = Record<string | number, any>;

/*
  Note on "(string & {})"
  This allows us to take `string` as an argument but still get useful autocomplete hints.
*/
// eslint-disable-next-line @typescript-eslint/ban-types
type ItemTypeKey<ItemType extends ItemRecord = ItemRecord> = keyof ItemType | (string & {});

export type UseSortStateType<ItemType extends ItemRecord = ItemRecord> = {
  items: ItemType[];
  sortConfig: { key: ItemTypeKey<ItemType> | string; direction?: 'ascending' | 'descending' };
};

export type UseSortConfigType<ItemType extends ItemRecord = ItemRecord> = {
  initialState?: { key: ItemTypeKey<ItemType> | string; direction?: 'ascending' | 'descending' };
  onSort?: (data: ItemType[]) => void;
  sort?: (data: ItemType[], sortConfig: UseSortStateType<ItemType>['sortConfig']) => ItemType[];
};

export type UseSortActionsType<ItemType extends ItemRecord = ItemRecord> = {
  sort: (key: ItemTypeKey<ItemType>) => void;
  getDirection: (key: ItemTypeKey<ItemType>) => 'ascending' | 'descending' | undefined;
  disableSorting: () => void;
  getPinProps: (
    key: ItemTypeKey<ItemType>,
  ) => {
    onClick: (event: MouseEvent<HTMLSpanElement>) => void;
    direction?: 'ascending' | 'descending';
  };
};

export type UseSortType<ItemType extends ItemRecord = ItemRecord> = [
  UseSortStateType<ItemType>,
  UseSortActionsType<ItemType>,
];

const isSortConfigValid = <ItemType extends ItemRecord>(config: UseSortStateType<ItemType>['sortConfig']) =>
  config.key && config.direction;

const sortData = <ItemType extends ItemRecord>(
  data: UseSortStateType<ItemType>['items'],
  config: UseSortStateType<ItemType>['sortConfig'],
  options: UseSortConfigType<ItemType>,
) => {
  if (!isSortConfigValid(config)) {
    return data;
  }

  if (options.sort) {
    return options.sort(data, config);
  }

  function maybeNumber(val: string) {
    return !isNaN(+String(val)) ? +String(val) : val;
  }

  return [...data].sort((a, b) => {
    const aVal = maybeNumber(a[config.key]);
    const bVal = maybeNumber(b[config.key]);
    if (aVal < bVal) {
      return config.direction === 'ascending' ? -1 : 1;
    }
    if (aVal > bVal) {
      return config.direction === 'ascending' ? 1 : -1;
    }
    return 0;
  });
};

const useSort = <ItemType extends ItemRecord>(
  data: ItemType[],
  config: UseSortConfigType<ItemType> = {},
): UseSortType<ItemType> => {
  const { initialState = { key: '', direction: undefined } } = config;

  const [sortConfig, setSortConfig] = React.useState(initialState);
  const [firstRender, setFirstRender] = React.useState(true);

  React.useEffect(() => {
    /*
      On the component's first render, we want to use this hook to sort the data in accordance
      with the sort config. Therefore, if it is not the first render, fail out early.
      The data can still be sorted after the first render when the sortable table headers
      invoke the sort() method below.
    */
    if (!firstRender) {
      return;
    }

    /*
      An onSort method can be passed into the config for the case where the component
      needs to be controlled (aka, with OrderableBody/OrderableRow). If the component
      is uncontrolled, fail out early.
    */
    if (!config.onSort) {
      return;
    }

    /*
      If the sort config is not valid, we cannot sort the data, so we fail out early.
    */
    if (!isSortConfigValid(sortConfig)) {
      return;
    }

    config.onSort(sortData(data, sortConfig, config));
    setFirstRender(false);
  }, [firstRender, config, data, sortConfig]);

  const sort = (key: Parameters<UseSortActionsType<ItemType>['sort']>[0]) => {
    const direction = sortConfig?.key === key && sortConfig?.direction === 'ascending' ? 'descending' : 'ascending';

    setSortConfig({ key, direction });

    if (config.onSort) {
      config.onSort(sortData(data, { key, direction }, config));
    }
  };

  const getDirection = (
    key: Parameters<UseSortActionsType<ItemType>['getDirection']>[0],
  ): 'ascending' | 'descending' | undefined => {
    if (!isSortConfigValid(sortConfig)) {
      return;
    }

    if (sortConfig.key === key) {
      return sortConfig.direction;
    }

    return undefined;
  };

  const getPinProps = (key: Parameters<UseSortActionsType<ItemType>['getPinProps']>[0]) => {
    return {
      onClick: () => sort(key),
      direction: getDirection(key),
    };
  };

  const disableSorting = () => setSortConfig({ key: '', direction: undefined });

  const state: UseSortStateType<ItemType> = {
    items: sortData(data, sortConfig, config),
    sortConfig,
  };

  const actions: UseSortActionsType<ItemType> = {
    sort,
    getDirection,
    getPinProps,
    disableSorting,
  };

  return [state, actions];
};

export default useSort;
